/* eslint-disable no-restricted-syntax */

import { buildURLSearchParams } from '@lib/utils'
import { houseSearchType, propertyType } from '@lib/config'
import { isArray, map, omit } from 'lodash-es'

/**
 * 从query中获取地图默认经纬度、zoom数据
 */
export function getMapboxDefaultViewState(query) {
  const { zoom: z, center } = query || {}
  if(/\d+/.test(z) && /[\d,]+/.test(center)) {
    const [lng, lat] = center.split(',')
    return {
      zoom: parseFloat(z),
      longitude: parseFloat(lng),
      latitude: parseFloat(lat),
    }
  }
  
  return null
}

/**
 * 获得地图rect的请求参数
 */
export function getMapboxBounds(mapbox) {
  const tmp = {}
  if(mapbox?.getBounds) {
    const b = mapbox.getBounds()
    // eslint-disable-next-line no-underscore-dangle
    tmp.rect = [b._sw.lng, b._ne.lng, b._sw.lat, b._ne.lat].join(',')
  }

  return tmp
}

export function replaceState(url, config, addQuery = false) {
  const q = buildURLSearchParams(config.query) 
  const fullUrl = addQuery 
    ? (q ? `${url}?${q}` : url)
    : url

  config.router.replace(fullUrl)
}

/**
 * 
 * @param {Object} config useListing()
 */
export function replaceListingUrl({ mapbox, config, router }) {
  const { searchParam, type, query } = config
  
  const zoom = mapbox.getZoom()
  const center = mapbox.getCenter().toArray().join(',')
  const { rect } = getMapboxBounds(mapbox)
  
  router.replaceUrl({ 
    type, 
    param: { ...searchParam, page: 1 }, 
    query: { ...query, zoom, center, rect } 
  })
}

/**
 * 鼠标经过房源卡片
 */
export function listingCardHover({ mps, data, type }) {
  let lng
  let lat
  const { id } = data

  if(type === 'estimate') {
    lng = data.lng
    lat = data.lat
  } else {
    const { longitude, latitude } = data
    if(longitude && latitude) {
      lng = parseFloat(longitude)
      lat = parseFloat(latitude)
    }
  }
  
  mps.setPopups([])
  if(lng && lat && id) {
    mps.setSpecialPopup({
      type,
      data: { lng, lat, points: [{ id, lng, lat }] }
    })
  }
}

/**
 * 点击地图上的 marker，在左边列表展示相关房源
 */
export function mapboxMarkerClick(markers, config, router) {
  const { type: typ, points } = markers || {}
  if(!points || (isArray(points) && points.length < 2)) {
    return
  }

  const typeMaps = { stateHouse: 'estimate', rental: 'rent' }
  const { type, searchParam, query } = config

  const newQuery = {
    ...query,
    markerIds: `${typeMaps[typ] || typ}_${map(points, 'id').join(',')}`
  }

  router.pushUrl({ type, param: searchParam, query: newQuery })
}

/**
 * 根据房源类型，设置默认显示的 marker
 */
export function setDefaultMarker(type, mps, streetId) {
  const hst = houseSearchType

  mps.setSold(type === hst.sold || !!streetId)
  mps.setEstimate(type === hst.estimate || type === hst.development || !!streetId)
  mps.setRental(type === hst.rent)
  mps.setRural(type === hst.rural)
  mps.setCommercialForSale(type === hst.commercialForSale)
  mps.setCommercialForLease(type === hst.commercialForLease)
  mps.setProperty(type === hst.residential || type === hst.newHomes)
}

/**
 * 设置api参数
 * 如果存在 query.react参数，说明是按地图可视区域进行搜索
 * 去掉 suburbId, regionId, districtId, streetId, schoolId有关地域的参数
 */
export function getApiParam(query, apiParam) {
  return query.rect
    ? {
      ...omit(apiParam, ['regionId', 'districtId', 'suburbId']),
      rect: query.rect
    }
    : apiParam
}

export function getRouterParam({ type, param, actionType, searchParam }) {
  const p = { type, param, query: {} }

  if(actionType === 'input') {
    p.param = {
      ...omit(searchParam, ['region', 'district', 'suburb', 'school', 'street']),
      ...param
    }
  }

  return p
}

/**
 * 根据 residential, rental, sold, property房源数量
 * 返回对应的api参数
 */
export function getApiParamsByCount(counts, pageSize, page) {
  const pt = propertyType
  const ht = houseSearchType

  const result = {
    residential: { offset: 0, limit: 0, typeId: pt.RESIDENTIAL, type: ht.residential },
    rental: { offset: 0, limit: 0, typeId: pt.RENTAL, type: ht.rental },
    sold: { offset: 0, limit: 0, typeId: pt.SOLD, type: ht.sold },
    property: { offset: 0, limit: 0, typeId: pt.ESTIMATE, type: ht.estimate }
  }

  const categories = ['residential', 'rental', 'sold', 'property']
  let startIndex = (page - 1) * pageSize
  let remaining = pageSize

  for (const category of categories) {
    const count = counts?.[`${category}Count`] || 0
    if (startIndex < count) {
      result[category].offset = startIndex
      result[category].limit = Math.min(remaining, count - startIndex)
      remaining -= result[category].limit
    }

    startIndex = Math.max(0, startIndex - count)
    if (remaining === 0) break
  }

  return {
    result,
    categories
  }
}
